import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChannelActivityListItem,
  communityChannelActivityApiClient,
} from '@src/api/tenant/online_community/communityChannelActivityApiClient';
import { PeriodRequest } from '@src/api/types/communityDashboard';
import { Pagination } from '@src/api/types/pagination';
import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import {
  ChannelVisibility,
  ChannelVisibilityEnum,
} from '@src/features/shared/community/enums/channelVisibility';
import SummaryDateRangeSelector from '@src/features/tenant/community/component/SummaryDateRangeSelector/SummaryDateRangeSelector';
import { getPresetByKey } from '@src/modules/constants/dateRangePreset';
import InputSearch from '@src/ui/elements/form/InputSearch';
import Title from '@src/ui/elements/typography/Title';
import logger from '@src/utils/logger';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';

import DataTable, { Column } from './component/DataTable';

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  preset: PeriodRequest | null;
};

const channelColumns: Column[] = [
  { title: '', key: 'id', width: 'w-[52px]' },
  { title: 'チャンネル名', key: 'channel_name', width: 'min-w-[162px] lg:w-[162px]' },
  {
    title: '公開設定',
    key: 'visibility',
    width: 'w-[120px]',
    sortable: true,
  },
  {
    title: '作成日',
    key: 'channel_created_at',
    width: 'w-[150px]',
    sortable: true,
  },
  {
    title: '参加人数',
    key: 'members_count',
    width: 'w-[120px]',
    sortable: true,
  },
  {
    title: '投稿数',
    key: 'posts',
    width: 'w-[100px]',
    sortable: true,
  },
  {
    title: 'リアクション数',
    key: 'reactions',
    width: 'w-[150px]',
    sortable: true,
  },
  {
    title: '総アクション数',
    key: 'total_actions',
    width: 'w-[150px]',
    sortable: true,
  },
  {
    title: '最新の投稿日',
    key: 'latest_posted_at',
    width: 'w-[150px]',
    sortable: true,
  },
] as const;

function CommunityChannelActivityListForm() {
  const INITIAL_PRESET: PeriodRequest = '7';
  const INITIAL_START_DATE = dayjs().subtract(7, 'day').toDate();
  const INITIAL_END_DATE = dayjs().subtract(1, 'day').toDate();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [preset, setPreset] = useState<PeriodRequest>(INITIAL_PRESET);
  const [startDate, setStartDate] = useState<Date | null>(INITIAL_START_DATE);
  const [endDate, setEndDate] = useState<Date | null>(INITIAL_END_DATE);
  const [activities, setActivities] = useState<ChannelActivityListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [sortField, setSortField] = useState('channel_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [keyword, setKeyword] = useState('');
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const currentPresetLabel = getPresetByKey(preset).label;
  const displayStartDate = startDate ?? INITIAL_START_DATE;
  const displayEndDate = endDate ?? INITIAL_END_DATE;
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [availableDateRange, setAvailableDateRange] = useState<{
    earliest: Date | null;
    latest: Date | null;
  }>({ earliest: null, latest: null });

  const state = location.state as DateRange;

  const loadActivities = async (
    pageNumber = 1,
    orderField = 'total_actions',
    orderDirection: 'asc' | 'desc' = 'desc',
    searchKeyword = '',
  ) => {
    try {
      const effectiveStartDate = state?.startDate ?? startDate;
      const effectiveEndDate = state?.endDate ?? endDate;
      const effectivePreset = state?.preset ?? preset;

      const parameters = {
        page: pageNumber.toString(),
        sort: orderField,
        direction: orderDirection,
        keyword: searchKeyword || undefined,
        start_date: effectiveStartDate ? dayjs(effectiveStartDate).format('YYYY-MM-DD') : undefined,
        end_date: effectiveEndDate ? dayjs(effectiveEndDate).format('YYYY-MM-DD') : undefined,
        period: effectivePreset,
      };
      const apiResult = await communityChannelActivityApiClient.index(parameters);
      handleApiResponse(apiResult, (responseData) => {
        setActivities(responseData.data);
        setPagination(responseData.pagination);
        setFilteredCount(responseData.pagination?.total ?? 0);
        setSearchedKeyword(searchKeyword);
        setAvailableDateRange({
          earliest: responseData.available_date_range?.earliest
            ? dayjs(responseData.available_date_range.earliest).toDate()
            : null,
          latest: responseData.available_date_range?.latest
            ? dayjs(responseData.available_date_range.latest).toDate()
            : null,
        });

        const { type, start_date, end_date } = responseData.period || {};
        const validation = responseData.validation_messages ?? [];

        const isAll = type === 'all';
        const shouldOverwriteDate = isAll || validation.length > 0;

        if (shouldOverwriteDate) {
          const startDate =
            isAll && responseData.available_date_range?.earliest
              ? dayjs(responseData.available_date_range.earliest).toDate()
              : start_date
                ? dayjs(start_date).toDate()
                : null;

          const endDate =
            isAll && responseData.available_date_range?.latest
              ? dayjs(responseData.available_date_range.latest).toDate()
              : end_date
                ? dayjs(end_date).toDate()
                : null;

          setPreset(type as PeriodRequest);
          setStartDate(startDate);
          setEndDate(endDate);
          setValidationMessages(validation);

          if (isAll) {
            setIsRangeOpen(false);
          }
        } else {
          setValidationMessages([]);
          setIsRangeOpen(false);
        }
      });
    } catch (error) {
      logger.error('Failed to load channel activities:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const currentPage = 1;
      loadActivities(currentPage, sortField, sortDirection, keyword);
      navigate({
        search: new URLSearchParams({
          page: '1',
          sort: sortField,
          direction: sortDirection,
          keyword,
        }).toString(),
      });
    }
  };

  const handleKeywordSearch = () => {
    const currentPage = 1;
    loadActivities(currentPage, sortField, sortDirection, keyword);
    navigate({
      search: new URLSearchParams({
        page: '1',
        sort: sortField,
        direction: sortDirection,
        keyword,
      }).toString(),
    });
  };

  const handleClearSearch = () => {
    setKeyword('');
    setSearchedKeyword('');
    setFilteredCount(null);
    navigate({ search: '' });
  };

  useEffect(() => {
    if (state) {
      setStartDate(state.startDate);
      setEndDate(state.endDate);
      if (state.preset) {
        setPreset(state.preset);
      }
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('page', '1');
      searchParams.set('start_date', dayjs(state.startDate).format('YYYY-MM-DD'));
      searchParams.set('end_date', dayjs(state.endDate).format('YYYY-MM-DD'));
      if (state.preset) {
        searchParams.set('preset', state.preset);
      }
    }
  }, [state]);

  useEffect(() => {
    const queryParameters = new URLSearchParams(location.search);
    const currentPage = parseInt(queryParameters.get('page') || '1', 10);
    const currentSort = queryParameters.get('sort') || 'total_actions';
    const currentDirection = (queryParameters.get('direction') as 'asc' | 'desc') || 'desc';
    const currentKeyword = queryParameters.get('keyword') || '';
    setSortField(currentSort);
    setSortDirection(currentDirection);
    setKeyword(currentKeyword);
    loadActivities(currentPage, currentSort, currentDirection, currentKeyword);
  }, [location.search]);

  const updateQuery = (params: Record<string, string>) => {
    const queryParameters = new URLSearchParams(location.search);
    Object.entries(params).forEach(([key, value]) => queryParameters.set(key, value));
    navigate({ search: queryParameters.toString() });
  };

  const handlePageChange = (pageNumber: number) => {
    updateQuery({ page: pageNumber.toString() });
  };

  const handleTableSort = (key: string, direction: 'asc' | 'desc') => {
    updateQuery({ sort: key, direction, page: '1' });
  };

  const handleDownloadCsv = async () => {
    setIsCsvLoading(true);
    try {
      const params = {
        sort: sortField,
        direction: sortDirection,
        keyword: searchedKeyword || undefined,
        start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
        end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      };
      const apiResult = await communityChannelActivityApiClient.csvExport(params);
      handleApiResponse<Blob, never>(apiResult, (csvBlob) => {
        const blobUrl = window.URL.createObjectURL(csvBlob);
        const anchorElement = document.createElement('a');
        anchorElement.href = blobUrl;
        const startDateStr = dayjs(displayStartDate).format('YYYYMMDD');
        const endDateStr = dayjs(displayEndDate).format('YYYYMMDD');
        const filenamePrefix = 'チャンネルアクティビティ一覧';
        const filenameSuffix = searchedKeyword ? `_${searchedKeyword}` : '';
        anchorElement.download = `${filenamePrefix}${filenameSuffix}_${startDateStr}-${endDateStr}.csv`;
        anchorElement.click();
        window.URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      logger.error('Failed to download CSV:', error);
    } finally {
      setIsCsvLoading(false);
    }
  };

  const handleRangeApply = ({
    startDate: newStart,
    endDate: newEnd,
    preset: newPreset,
  }: {
    startDate: Date | null;
    endDate: Date | null;
    preset: PeriodRequest | null;
  }) => {
    setValidationMessages([]);
    setStartDate(newStart);
    setEndDate(newEnd);
    if (newPreset) {
      setPreset(newPreset);
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', '1');
    if (newStart) {
      searchParams.set('start_date', dayjs(newStart).format('YYYY-MM-DD'));
    }
    if (newEnd) {
      searchParams.set('end_date', dayjs(newEnd).format('YYYY-MM-DD'));
    }
    if (newPreset) {
      searchParams.set('preset', newPreset);
    }
    navigate({ search: searchParams.toString() });
  };

  const tableData = activities.map((activity, index) => ({
    id: pagination ? pagination.from + index : index + 1,
    channel_name: activity.channel_name,
    visibility: ChannelVisibilityEnum[activity.visibility as ChannelVisibility],
    channel_created_at: activity.channel_created_at,
    members_count: activity.members_count,
    posts: activity.posts,
    reactions: activity.reactions,
    total_actions: activity.total_actions,
    latest_posted_at: activity.latest_posted_at,
  }));

  return (
    <main>
      <SpinnerOverlay loading={isCsvLoading} />
      <div className='py-[7px]'>
        <div className='flex items-center justify-between'>
          <Title>チャンネル別アクティビティ一覧</Title>
          <div className='w-[250px]'>
            <InputSearch
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              onSubmit={handleKeywordSearch}
              onClear={handleClearSearch}
            />
          </div>
        </div>
        <div className='mt-[10px] flex items-center justify-end'>
          <div className='mr-3 text-xs'>
            {`${dayjs(displayStartDate).format('YYYY年MM月DD日')} - ${dayjs(displayEndDate).format('YYYY年MM月DD日')}`}
          </div>

          <div className='relative mr-3'>
            <button
              className='w-full appearance-none rounded-[4px] border border-solid border-gray-250
                 bg-down bg-[center_right_12px] bg-no-repeat py-[10px] pl-6 pr-[64px]
                 text-left text-sm font-medium'
              onClick={() => setIsRangeOpen(!isRangeOpen)}
            >
              {currentPresetLabel}
            </button>

            {isRangeOpen && (
              <div className='absolute right-0 top-[50px] z-[9999]'>
                <SummaryDateRangeSelector
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                  initialPreset={preset}
                  availableDateRange={availableDateRange}
                  onChange={handleRangeApply}
                  onClose={() => setIsRangeOpen(false)}
                  errorMessages={validationMessages}
                />
              </div>
            )}
          </div>

          <button
            className='grid h-[42px] w-[48px] place-items-center rounded-[4px] bg-primary-500'
            onClick={handleDownloadCsv}
          >
            <SvgIconDownload
              color='#fff'
              style={{ width: '24px', height: '24px' }}
            />
          </button>
        </div>
      </div>
      {searchedKeyword && (
        <p className='overflow-wrap-anywhere mt-2 text-sm'>
          「{searchedKeyword}」で絞り込んだ結果：{filteredCount}件
        </p>
      )}
      <div className='relative min-h-[500px]'>
        <DataTable
          columns={channelColumns}
          data={tableData}
          marginTop='mt-4'
          padding='p-0'
          onSort={handleTableSort}
          sortKey={sortField}
          sortDirection={sortDirection}
          pagination={pagination || undefined}
          keyword={searchedKeyword}
          handlePageChange={handlePageChange}
        />
        {isTableLoading && (
          <div className='absolute inset-0 flex items-center justify-center bg-white/60'>
            <ClipLoader
              size={50}
              color={brandColors['500']}
              loading={isTableLoading}
            />
          </div>
        )}
      </div>
    </main>
  );
}

export default CommunityChannelActivityListForm;
